import * as React from 'react';
import { createDirtyStateSnapshot, type DirtyStateSerializationOptions } from '../lib/dirtyState';

interface UpdateCurrentOptions {
  resetBaseline?: boolean;
}

interface UseDirtyStateOptions<T> extends DirtyStateSerializationOptions<T> {
  debounceMs?: number;
}

interface DirtyStateHandle<T> {
  isDirty: boolean;
  setBaseline: (payload: T) => void;
  updateCurrent: (payload: T, options?: UpdateCurrentOptions) => void;
  reset: () => void;
}

export function useDirtyState<T>(options: UseDirtyStateOptions<T> = {}): DirtyStateHandle<T> {
  const { debounceMs = 0, ignorePaths, normalize, trimStrings } = options;
  const [isDirty, setIsDirty] = React.useState(false);
  const baselinePayloadRef = React.useRef<T | null>(null);
  const currentPayloadRef = React.useRef<T | null>(null);
  const baselineSnapshotRef = React.useRef<string | null>(null);
  const currentSnapshotRef = React.useRef<string | null>(null);
  const debounceTimerRef = React.useRef<number | null>(null);

  const buildSnapshot = React.useCallback(
    (payload: T) =>
      createDirtyStateSnapshot(payload, {
        ignorePaths,
        normalize,
        trimStrings,
      }),
    [ignorePaths, normalize, trimStrings]
  );

  const clearDebounce = React.useCallback(() => {
    if (debounceTimerRef.current === null) return;
    window.clearTimeout(debounceTimerRef.current);
    debounceTimerRef.current = null;
  }, []);

  const applyCurrent = React.useCallback(
    (payload: T, resetBaseline: boolean) => {
      const nextSnapshot = buildSnapshot(payload);
      currentPayloadRef.current = payload;
      currentSnapshotRef.current = nextSnapshot;

      if (resetBaseline || baselineSnapshotRef.current === null) {
        baselinePayloadRef.current = payload;
        baselineSnapshotRef.current = nextSnapshot;
        setIsDirty(false);
        return;
      }

      setIsDirty(nextSnapshot !== baselineSnapshotRef.current);
    },
    [buildSnapshot]
  );

  const setBaseline = React.useCallback(
    (payload: T) => {
      clearDebounce();
      applyCurrent(payload, true);
    },
    [applyCurrent, clearDebounce]
  );

  const updateCurrent = React.useCallback(
    (payload: T, updateOptions?: UpdateCurrentOptions) => {
      const resetBaseline = updateOptions?.resetBaseline ?? false;
      currentPayloadRef.current = payload;
      clearDebounce();

      if (resetBaseline || debounceMs <= 0) {
        applyCurrent(payload, resetBaseline);
        return;
      }

      debounceTimerRef.current = window.setTimeout(() => {
        debounceTimerRef.current = null;
        applyCurrent(currentPayloadRef.current ?? payload, false);
      }, debounceMs);
    },
    [applyCurrent, clearDebounce, debounceMs]
  );

  const reset = React.useCallback(() => {
    clearDebounce();
    currentPayloadRef.current = baselinePayloadRef.current;
    currentSnapshotRef.current = baselineSnapshotRef.current;
    setIsDirty(false);
  }, [clearDebounce]);

  React.useEffect(() => {
    if (baselinePayloadRef.current === null || currentPayloadRef.current === null) return;
    clearDebounce();
    const baselineSnapshot = buildSnapshot(baselinePayloadRef.current);
    const currentSnapshot = buildSnapshot(currentPayloadRef.current);
    baselineSnapshotRef.current = baselineSnapshot;
    currentSnapshotRef.current = currentSnapshot;
    setIsDirty(currentSnapshot !== baselineSnapshot);
  }, [buildSnapshot, clearDebounce]);

  React.useEffect(() => () => clearDebounce(), [clearDebounce]);

  return React.useMemo(
    () => ({
      isDirty,
      setBaseline,
      updateCurrent,
      reset,
    }),
    [isDirty, reset, setBaseline, updateCurrent]
  );
}
